import { useEffect, useRef, useState, FormEvent } from "react";
import { vesselsApi, Vessel } from "../api/vessels";
import { sha256Hex } from "../lib/crypto";
import { authApi } from "../api/auth";

interface VesselForm {
  name: string;
  imoNumber: string;
  flag: string;
  vesselType: string;
  contactEmail: string;
  password: string;
  confirmPassword: string;
}

const EMPTY_FORM: VesselForm = { name: "", imoNumber: "", flag: "", vesselType: "", contactEmail: "", password: "", confirmPassword: "" };

type EmailState = "empty" | "invalid" | "incomplete" | "valid";

function validateEmail(email: string): { state: EmailState; message?: string } {
  if (!email) return { state: "empty" };
  if (!email.includes("@"))
    return { state: "invalid", message: "Please enter a valid email address (must include @)" };
  const afterAt = email.split("@")[1] ?? "";
  if (!afterAt.includes("."))
    return { state: "incomplete", message: "Email looks incomplete — check the domain (e.g. master@vessel.com)" };
  return { state: "valid" };
}

function emailBorderClass(state: EmailState) {
  if (state === "invalid")    return "border-red-500";
  if (state === "incomplete") return "border-yellow-400";
  if (state === "valid")      return "border-green-500";
  return "border-gray-300";
}

const INPUT_BASE =
  "w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500";

export default function VesselsPage() {
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ msg: string; kind: "success" | "error" } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function showToast(msg: string, kind: "success" | "error" = "success") {
    setToast({ msg, kind });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }

  // ── Add modal ─────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<VesselForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // ── Edit modal ────────────────────────────────────────────────────────────
  const [editingVessel, setEditingVessel] = useState<Vessel | null>(null);
  const [editForm, setEditForm] = useState<VesselForm>(EMPTY_FORM);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // ── Delete confirmation ───────────────────────────────────────────────────
  const [deletingVessel, setDeletingVessel] = useState<Vessel | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // ── Copy login link ───────────────────────────────────────────────────────
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function copyLoginLink(vesselId: string) {
    navigator.clipboard.writeText("https://seavault.uk/vessel/login").then(() => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
      setCopiedId(vesselId);
      copyTimer.current = setTimeout(() => setCopiedId(null), 2000);
    });
  }

  // ── Derived email validation ───────────────────────────────────────────────
  const addEmailV  = validateEmail(form.contactEmail);
  const editEmailV = validateEmail(editForm.contactEmail);

  function loadVessels() {
    vesselsApi.list().then(setVessels).catch(console.error);
  }

  useEffect(() => {
    loadVessels();
    authApi.me().then((u) => setIsSuperAdmin(u.is_super_admin)).catch(console.error);
  }, []);

  // ── Add vessel ─────────────────────────────────────────────────────────────
  function openModal() {
    setForm(EMPTY_FORM);
    setAddError(null);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setAddError(null);
    if (form.password !== form.confirmPassword) {
      setAddError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      const passwordHash = await sha256Hex(form.password);
      await vesselsApi.create({
        name: form.name,
        imoNumber: form.imoNumber || undefined,
        flag: form.flag || undefined,
        vesselType: form.vesselType || undefined,
        contactEmail: form.contactEmail,
        passwordHash,
      });
      closeModal();
      loadVessels();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : "Failed to add vessel");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Edit vessel ────────────────────────────────────────────────────────────
  function openEditModal(v: Vessel) {
    setEditForm({
      name: v.name,
      imoNumber: v.imo_number ?? "",
      flag: v.flag ?? "",
      vesselType: v.vessel_type ?? "",
      contactEmail: v.contact_email ?? "",
      password: "",
      confirmPassword: "",
    });
    setShowChangePassword(false);
    setEditingVessel(v);
  }

  function closeEditModal() {
    setEditingVessel(null);
    setShowChangePassword(false);
  }

  async function handleEditSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editingVessel) return;
    if (showChangePassword && editForm.password !== editForm.confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }
    setEditSubmitting(true);
    try {
      const patch: Parameters<typeof vesselsApi.update>[1] = {
        name: editForm.name,
        imoNumber: editForm.imoNumber || undefined,
        flag: editForm.flag || undefined,
        vesselType: editForm.vesselType || undefined,
        contactEmail: editForm.contactEmail,
      };
      if (showChangePassword && editForm.password) {
        patch.passwordHash = await sha256Hex(editForm.password);
      }
      await vesselsApi.update(editingVessel.id, patch);
      setVessels((vs) =>
        vs.map((v) =>
          v.id === editingVessel.id
            ? {
                ...v,
                name: editForm.name,
                imo_number: editForm.imoNumber || null,
                flag: editForm.flag || null,
                vessel_type: editForm.vesselType || null,
                contact_email: editForm.contactEmail,
              }
            : v
        )
      );
      closeEditModal();
      showToast("Vessel updated");
    } catch {
      showToast("Failed to update vessel", "error");
    } finally {
      setEditSubmitting(false);
    }
  }

  // ── Delete vessel ──────────────────────────────────────────────────────────
  async function handleDelete() {
    if (!deletingVessel) return;
    setDeleteSubmitting(true);
    try {
      await vesselsApi.remove(deletingVessel.id);
      setVessels((vs) => vs.filter((v) => v.id !== deletingVessel.id));
      setDeletingVessel(null);
      showToast("Vessel deleted");
    } catch {
      showToast("Failed to delete vessel", "error");
      setDeletingVessel(null);
    } finally {
      setDeleteSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[60] rounded-lg px-5 py-3 text-sm font-medium text-white shadow-lg ${
          toast.kind === "error" ? "bg-red-600" : "bg-green-600"
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Page header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Vessel Registry</h2>
          <p className="text-sm text-gray-500 mt-0.5">{vessels.length} vessel{vessels.length !== 1 ? "s" : ""} registered</p>
        </div>
        <button
          onClick={openModal}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Add Vessel
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">IMO</th>
                {isSuperAdmin && (
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Company</th>
                )}
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {vessels.map((v) => (
                <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3 font-medium text-gray-900">{v.name}</td>
                  <td className="px-5 py-3 text-gray-500 font-mono text-xs">{v.imo_number ?? "—"}</td>
                  {isSuperAdmin && (
                    <td className="px-5 py-3 text-gray-500 text-xs">{v.company_name ?? "—"}</td>
                  )}
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      v.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                    }`}>
                      {v.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-4">
                      <button
                        onClick={() => copyLoginLink(v.id)}
                        className={`flex items-center gap-1 text-xs font-medium transition-colors ${
                          copiedId === v.id
                            ? "text-green-600"
                            : "text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        {copiedId === v.id ? (
                          <>
                            <svg viewBox="0 0 18 18" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="2 9 7 14 16 4"/>
                            </svg>
                            Copied
                          </>
                        ) : (
                          <>
                            <svg viewBox="0 0 18 18" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M7 8a3 3 0 0 0 4.243 0l2.121-2.121a3 3 0 0 0-4.243-4.243L7.94 2.818"/>
                              <path d="M11 10a3 3 0 0 0-4.243 0L4.636 12.12a3 3 0 0 0 4.243 4.243l1.06-1.06"/>
                            </svg>
                            Copy link
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => openEditModal(v)}
                        className="text-xs font-medium text-navy-700 hover:text-navy-900 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeletingVessel(v)}
                        className="text-xs font-medium text-red-600 hover:text-red-800 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {vessels.length === 0 && (
                <tr>
                  <td colSpan={isSuperAdmin ? 5 : 4} className="px-5 py-10 text-center text-sm text-gray-400">No vessels registered</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Delete confirmation modal ─────────────────────────────────────── */}
      {deletingVessel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) setDeletingVessel(null); }}
        >
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete vessel?</h2>
            <p className="text-sm text-gray-600 mb-6">
              Delete <span className="font-medium text-gray-900">{deletingVessel.name}</span>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeletingVessel(null)}
                className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteSubmitting}
                className="flex-1 rounded-lg bg-red-600 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteSubmitting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Vessel modal ─────────────────────────────────────────────── */}
      {editingVessel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) closeEditModal(); }}
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-gray-900">Edit Vessel</h2>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vessel Name <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="MV Example"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IMO Number</label>
                <input
                  value={editForm.imoNumber}
                  onChange={(e) => setEditForm((f) => ({ ...f, imoNumber: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="IMO 1234567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flag</label>
                <input
                  value={editForm.flag}
                  onChange={(e) => setEditForm((f) => ({ ...f, flag: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="e.g. Bahamas"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vessel Type</label>
                <input
                  value={editForm.vesselType}
                  onChange={(e) => setEditForm((f) => ({ ...f, vesselType: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="e.g. Bulk Carrier"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editForm.contactEmail}
                  onChange={(e) => setEditForm((f) => ({ ...f, contactEmail: e.target.value }))}
                  className={`${INPUT_BASE} ${emailBorderClass(editEmailV.state)}`}
                  placeholder="captain@example.com"
                />
                {editEmailV.state === "invalid" && (
                  <p className="mt-1 text-xs text-red-600">{editEmailV.message}</p>
                )}
                {editEmailV.state === "incomplete" && (
                  <p className="mt-1 text-xs text-yellow-600">{editEmailV.message}</p>
                )}
              </div>

              {/* Change password section */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword((v) => !v);
                    setEditForm((f) => ({ ...f, password: "", confirmPassword: "" }));
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  Change Portal Password
                  <span className="text-gray-400">{showChangePassword ? "▲" : "▼"}</span>
                </button>
                {showChangePassword && (
                  <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                      <input
                        type="password"
                        value={editForm.password}
                        onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))}
                        className={`${INPUT_BASE} border-gray-300`}
                        placeholder="New portal password"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                      <input
                        type="password"
                        value={editForm.confirmPassword}
                        onChange={(e) => setEditForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                        className={`${INPUT_BASE} ${
                          editForm.confirmPassword && editForm.password !== editForm.confirmPassword
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="Repeat new password"
                      />
                      {editForm.confirmPassword && editForm.password !== editForm.confirmPassword && (
                        <p className="mt-1 text-xs text-red-600">Passwords do not match</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    editSubmitting ||
                    editEmailV.state !== "valid" ||
                    (showChangePassword && (!editForm.password || editForm.password !== editForm.confirmPassword))
                  }
                  className="flex-1 rounded-lg bg-navy-700 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
                >
                  {editSubmitting ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add Vessel modal ──────────────────────────────────────────────── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-gray-900">Add Vessel</h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {addError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {addError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vessel Name <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="MV Example"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  IMO Number <span className="text-red-500">*</span>
                </label>
                <input
                  required
                  value={form.imoNumber}
                  onChange={(e) => setForm((f) => ({ ...f, imoNumber: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="IMO 1234567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Flag</label>
                <input
                  value={form.flag}
                  onChange={(e) => setForm((f) => ({ ...f, flag: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="e.g. Bahamas"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vessel Type</label>
                <input
                  value={form.vesselType}
                  onChange={(e) => setForm((f) => ({ ...f, vesselType: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="e.g. Bulk Carrier"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.contactEmail}
                  onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
                  className={`${INPUT_BASE} ${emailBorderClass(addEmailV.state)}`}
                  placeholder="captain@example.com"
                />
                {addEmailV.state === "invalid" && (
                  <p className="mt-1 text-xs text-red-600">{addEmailV.message}</p>
                )}
                {addEmailV.state === "incomplete" && (
                  <p className="mt-1 text-xs text-yellow-600">{addEmailV.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Portal Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className={`${INPUT_BASE} border-gray-300`}
                  placeholder="Vessel portal password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  value={form.confirmPassword}
                  onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                  className={`${INPUT_BASE} ${
                    form.confirmPassword && form.password !== form.confirmPassword
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Repeat password"
                />
                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <p className="mt-1 text-xs text-red-600">Passwords do not match</p>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    submitting ||
                    addEmailV.state !== "valid" ||
                    !form.password ||
                    form.password !== form.confirmPassword
                  }
                  className="flex-1 rounded-lg bg-navy-700 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
                >
                  {submitting ? "Adding…" : "Add Vessel"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
